import React, { useState } from 'react';
import { InputLabel, Grid, TextField, makeStyles } from '@material-ui/core';
import { useFormContext, useWatch } from 'react-hook-form';
import { useStyles } from './styles';
import { CustomizedButton } from '@components/Customized';
import {
  CapaServiceType,
  useVerifyEmailVerificationCodeQuery,
  CapaErrorCode
} from '@generated/graphql';
import { InputItem } from '@components/Input/Inputs';
import useUserActionLog, {
  Enum_Action_Button_Enum,
  CapaActivityType
} from '@hooks/useUserActionLog';
import { emailRegex } from '@utils/regex';

interface EmailInputProps {
  onClickAuthButton: (
    email: string,
    service: CapaServiceType,
    callback: () => void
  ) => void;
  emailAuthState: boolean;
  setEmailAuthState: (value: boolean) => void;
}

const EmailInput: React.FC<EmailInputProps> = ({
  onClickAuthButton,
  emailAuthState,
  setEmailAuthState
}: EmailInputProps) => {
  const [isAuthCodeSent, setIsAuthCodeSent] = useState<boolean>(false);
  const {
    inputContainer,
    emailContainer,
    emailContainer_authorized,
    authorizationButton,
    authInput,
    authInputNotchedOutline,
    authInputPlaceholder
  } = useStyles({ emailAuthState });
  const {
    register,
    errors,
    setValue,
    setError,
    getValues,
    clearErrors,
    control
  } = useFormContext();
  const { email } = useWatch<{ email: string }>({
    control
  });

  const { onInsertUserActionLogOne } = useUserActionLog();

  const { emailInputItem } = useSytles();

  const { refetch } = useVerifyEmailVerificationCodeQuery({
    skip: true
  });

  const handleClick = () => {
    const email = getValues('email');
    const service = getValues('service');

    if (!emailRegex.test(email)) {
      setError('email', { message: '유효하지 않은 이메일입니다.' });
      return;
    }

    onClickAuthButton(email, service, () => {
      setIsAuthCodeSent(true);
      if (errors.code) {
        clearErrors('code');
      }
    });
  };

  return (
    <>
      <InputLabel>이메일</InputLabel>
      <Grid
        container
        direction="row"
        justifyContent="space-between"
        className={inputContainer}
      >
        <Grid
          item
          className={
            emailAuthState ? emailContainer_authorized : emailContainer
          }
        >
          <InputItem
            className={emailInputItem}
            errors={errors}
            name="email"
            inputProps={{
              placeholder: '이메일 주소',
              onFocus: () => {
                onInsertUserActionLogOne(
                  Enum_Action_Button_Enum.Button_SignUp_Email,
                  CapaActivityType.ButtonClick
                );
              },
              onChange: () => {
                setIsAuthCodeSent(false);
                if (emailAuthState) {
                  setEmailAuthState(false);
                  setValue('code', '');
                  setError('code', {
                    message:
                      '이메일이 변경되었습니다. 인증을 다시 진행해주세요.'
                  });
                }
              },
              ref: register({
                maxLength: 30
              })
            }}
          />
        </Grid>
        {!emailAuthState && (
          <Grid item>
            <CustomizedButton
              variant="outlined"
              size="medium"
              disableElevation
              className={authorizationButton}
              disabled={!email?.length}
              onClick={handleClick}
            >
              {isAuthCodeSent ? '재전송' : '인증하기'}
            </CustomizedButton>
          </Grid>
        )}
      </Grid>
      <Grid
        container
        direction="row"
        justifyContent="space-between"
        className={inputContainer}
      >
        <Grid
          item
          className={
            isAuthCodeSent && !emailAuthState
              ? emailContainer
              : emailContainer_authorized
          }
        >
          <TextField
            name="code"
            fullWidth
            className={authInput}
            disabled={!isAuthCodeSent}
            error={!!errors.code?.message}
            size="medium"
            variant="outlined"
            helperText={
              emailAuthState
                ? '이메일 인증이 완료되었습니다'
                : errors.code?.message || ''
            }
            inputProps={{
              readOnly: !isAuthCodeSent || emailAuthState,
              placeholder: '인증번호를 입력해주세요.',
              minLength: 6,
              maxLength: 6,
              ref: register({
                required: true
              }),
              className: authInputPlaceholder,
              classes: { notchedOutline: authInputNotchedOutline }
            }}
          />
        </Grid>
        {isAuthCodeSent && !emailAuthState && (
          <Grid item>
            <CustomizedButton
              variant="outlined"
              color="primary"
              size="medium"
              disableElevation
              className={authorizationButton}
              onClick={(e) => {
                onInsertUserActionLogOne(
                  Enum_Action_Button_Enum.Button_SignUp_VerifyingCode,
                  CapaActivityType.ButtonClick
                );

                refetch({
                  email: getValues('email'),
                  code: getValues('code')
                }).then((result) => {
                  const resultCode =
                    result.data?.verifyEmailVerificationCode?.result;

                  switch (resultCode) {
                    case CapaErrorCode.Success: {
                      setEmailAuthState(true);
                      clearErrors('code');
                      return;
                    }
                    case CapaErrorCode.CodeMismatch: {
                      setError('code', {
                        message: '인증번호가 일치하지 않습니다.'
                      });
                      return;
                    }
                    case CapaErrorCode.ParmeterValidationError: {
                      setError('code', {
                        message: '유효하지 않은 인증번호입니다.'
                      });
                      return;
                    }
                    default: {
                      setError('code', {
                        message: '인증번호가 일치하지 않습니다.'
                      });
                      return;
                    }
                  }
                });
              }}
            >
              인증하기
            </CustomizedButton>
          </Grid>
        )}
      </Grid>
    </>
  );
};

export default EmailInput;

const useSytles = makeStyles({
  emailInputItem: {
    '& .MuiFormHelperText-root': {
      position: 'static',
      textAlign: 'left !important'
    }
  }
});
